import { Country } from 'library/ts/model/enums/geo/Country';
import { RuntimeVO } from 'library/ts/utilities/RuntimeVO';
import { DEFAULT_RELATIVE_VALUE, DefaultSmartTagReadyCallback } from 'tag/ts/smart_tag/default/DefaultSmartTagReadyCallback';
import { ISmartTagReadyCallback } from 'tag/ts/smart_tag/interfaces/ISmartTagReadyCallback';
import { ISmartTagRuntimeVO } from 'tag/ts/smart_tag/interfaces/ISmartTagRuntimeVO';
import { Constants } from 'tag/ts/smart_tag/model/Constants';
import { ParentEnvelope } from 'tag/ts/smart_tag/model/parent/envelope/ParentEnvelope';
import { ParentEnvelopeHostPattern } from 'tag/ts/smart_tag/model/parent/envelope/ParentEnvelopeHostPattern';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';

/**
 * @copyright © Paramount Streaming
 * @author Stan Gletski
 * @description RuntimeVO object used for storing base data used by all applications: Mapper, SmartTag, Skin
 */
export class SmartTagRuntimeVO extends RuntimeVO implements ISmartTagRuntimeVO {

	constructor() {
		super( 'smart-tag-debug', 'tag' );
	}

	/**
	 * @description Returns SmartTag ready callback object
	 * @return {ISmartTagReadyCallback}
	 */
	get readyCallback(): ISmartTagReadyCallback {
		// get onReady param appended to tag.js script URL
		// is true Smart Tag is going to load it regardless of content: PID or assetURL
		const onReadyParam: string = this.utils.URL.getQueryField( 'onReady', this.app.url );

		// relative load is ON by default
		const defaultRelativeValue: boolean = DEFAULT_RELATIVE_VALUE;

		// default callback object
		let callbackObject = DefaultSmartTagReadyCallback;

		// callback object passed in as onReady URL query param with smart tag import
		if (
			this.utils.Type.isString( onReadyParam )
			&&
			this.utils.String.trim( onReadyParam ).length > 0
			&&
			this.utils.Type.isBase64( onReadyParam )
		) {
			// decode param and merge into initial callback object
			callbackObject = this.utils.DeepMerge.deepmerge(
				callbackObject,
				this.utils.Convert.objectDecode( this.utils.String.trim( onReadyParam ) )
			);
		}

		switch ( this.utils.Type.isObject( callbackObject.payload ) ) {
			case false:
				callbackObject.payload = { relative: defaultRelativeValue };
				break;
			case true:
				if ( this.utils.Type.isBoolean( callbackObject.payload.relative ) === false ) {
					callbackObject.payload.relative = defaultRelativeValue;
				}
				break;
		}

		// get namespace object
		if ( this.utils.Type.isUndefined( callbackObject.namespace ) ) {
			callbackObject.namespace = window;
		} else {
			if ( this.utils.Type.isString( callbackObject.namespace ) ) {
				callbackObject.namespace = this.utils.Object.getDeepVal( window, callbackObject.namespace );
			}
		}

		// sanity check for undefined method name passed from page
		if ( this.utils.Type.isUndefined( callbackObject.method ) ) {
			callbackObject.method = Constants.getInstance().FUNCTION_SMART_TAG_ON_READY;
		}
		// trim method string
		callbackObject.method = this.utils.String.trim( callbackObject.method as string );
		//  get callback method reference
		callbackObject.method = this.utils.Object.getDeepVal( callbackObject.namespace, callbackObject.method );

		return callbackObject;
	}

	/**
	 * @description Returns parent application / site envelope
	 * @returns {ParentEnvelope}
	 */
	get parentEnvelope() {
		// type for tests array
		type THostTest = { pattern: ParentEnvelopeHostPattern, env: ParentEnvelope };

		// top window host lower-cased + trimmed
		const topHost = this.top.parsed.host.toLowerCase().trim();

		// list of test patterns and their corresponding return types (envelopes)
		const tests: THostTest[] = [];

		// Loop over ParentEnvelope enum
		Object.entries( ParentEnvelope ).forEach(
			( [ key, value ] ) => {
				// fill out tests array
				tests.push(
					{
						pattern: ParentEnvelopeHostPattern[ key ],
						env: value
					}
				);
			}
		);

		// instantiate our return value
		let returnEnvelope: ParentEnvelope = undefined;

		// loop over our tests
		for ( let i = 0; i < tests.length; i++ ) {
			const oneTest = tests[ i ];
			// if matching envelope test patter match is found
			if ( topHost.includes( oneTest.pattern ) ) {
				returnEnvelope = oneTest.env; // set return envelope value
				break;
			}
		}

		/**
		 * @description Custom use case for verification desktop prod
		 * @returns {boolean}
		 */
		const isInternationalDesktopProd = (): boolean => {
			let returnValue: boolean = undefined;

			// Check #1 => check if country is set in onReady callback data
			if ( this.utils.Type.isString( this?.readyCallback?.country ) && !this.utils.Type.isEmpty( this?.readyCallback?.country ) ) {
				returnValue = this?.readyCallback?.country === Country.US;
			}
			// If return value was not set in previous step
			if ( !this.utils.Type.isBoolean( returnValue ) ) {
				// Check #02 => check CBS.Registry
				if ( this.utils.Type.isBoolean( window?.CBS?.Registry?.region?.international ) ) {
					returnValue = window.CBS.Registry.region.international;
				}
			}
			// If return value was not set in previous step
			if ( !this.utils.Type.isBoolean( returnValue ) ) {
				// Check #03 => check body element class in DOM
				if ( this.utils.Type.isElement( document?.body ) ) {
					returnValue = !this.utils.CSS.hasClass( document?.body, 'property-us' );
				}
			}

			return returnValue;
		};

		// process any custom use cases
		// filter by embed type
		switch ( this?.readyCallback?.env ) {
			case EmbedType.DESKTOP:
			case EmbedType.MOBILE_WEB:
				// filter per return envelope value
				switch ( returnEnvelope ) {
					// desktop + US + International has the same domain
					case ParentEnvelope.PROD_PPLUS:
					case ParentEnvelope.PROD_INT:
						// process US vs Int context confirmed via other means
						if ( isInternationalDesktopProd() === true ) {
							returnEnvelope = ParentEnvelope.PROD_INT;
						}
						break;
				}
				break;
			case EmbedType.HTML5:
				// HTML5 specific processing logic goes here...
				break;
		}

		// if no other envelope is detected return prod
		return returnEnvelope;
	}

}